// src/components/navigation/NavItem.jsx
// Individual menu item component with badge/warning support

import React from 'react';
import { NavLink } from 'react-router-dom';
import Badge from 'react-bootstrap/Badge';

/**
 * NavItem - renders a single navigation menu item
 *
 * Props:
 * - item: Menu item object from navConfig
 * - badge: Badge text to display (or null)
 * - warning: Whether to show warning icon
 * - userType: 'installer' or 'dealer' (for external URLs)
 * - onNavigate: Callback when navigation occurs (for mobile drawer close)
 */
function NavItem({ item, badge, warning, userType, onNavigate }) {
  // External link (opens in new tab)
  if (item.externalUrl) {
    const url = item.urls[userType] || item.urls.installer;
    return (
      <li>
        <a
          className="text-light"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
        >
          {item.label}
        </a>
      </li>
    );
  }

  // Custom isActive check that includes query params
  const isActive = (match, loc) => {
    if (!match) return false;

    // Parse route and current location
    const [routePath, routeQuery] = item.route.split('?');
    const currentPath = loc.pathname;
    const currentQuery = loc.search.replace('?', '');

    // Path must match
    if (routePath !== currentPath) return false;

    // If route has query params, they must match exactly
    if (routeQuery) {
      return routeQuery === currentQuery;
    }

    // If route has no query params, only active if current URL also has no query params
    return !currentQuery;
  };

  // Internal route link
  return (
    <li>
      <NavLink
        activeClassName="active"
        className="text-light"
        to={item.route}
        isActive={isActive}
        onClick={onNavigate}
      >
        {badge && (
          <Badge pill variant="danger">
            {badge}
          </Badge>
        )}{" "}
        {warning && (
          <>
            <span className="nav-warning-icon" title="Action Required">⚠️</span>{" "}
          </>
        )}
        {item.label}
      </NavLink>
    </li>
  );
}

export default NavItem;
